import { canUseAnalytics } from './consent';

const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-VKE4SQFMFW';
let analyticsLoadPromise;

function ensureAnalyticsScript() {
  if (!canUseAnalytics() || !measurementId) {
    return Promise.resolve(false);
  }

  if (window.gtag) {
    return Promise.resolve(true);
  }

  if (analyticsLoadPromise) {
    return analyticsLoadPromise;
  }

  const existingScript = document.querySelector('script[data-analytics-loader="true"]');
  if (existingScript) {
    analyticsLoadPromise = Promise.resolve(true);
    return analyticsLoadPromise;
  }

  analyticsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.dataset.analyticsLoader = 'true';
    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, { anonymize_ip: true });
      resolve(true);
    };
    script.onerror = () => reject(new Error('Failed to load Google Analytics'));
    document.head.appendChild(script);
  });

  return analyticsLoadPromise;
}

export function initAnalytics() {
  return ensureAnalyticsScript().catch((err) => {
    console.error('Analytics loader error:', err);
    return false;
  });
}

export function trackEvent(eventName, eventParams = {}) {
  if (window.gtag && canUseAnalytics()) {
    window.gtag('event', eventName, eventParams);
  }
}

export function trackEvaluation(score, verdict, category) {
  trackEvent('evaluation_completed', {
    evaluation_score: score,
    verdict: verdict,
    category: category,
    timestamp: new Date().toISOString()
  });
}

export function trackFeedback(feedbackType, evaluation_id) {
  trackEvent('feedback_submitted', {
    feedback_type: feedbackType,
    evaluation_id: evaluation_id
  });
}

export function trackFormSubmit(formData) {
  trackEvent('evaluation_form_submitted', {
    payment_type: formData.payment_type,
    purchase_category: formData.purchase_category
  });
}

export function trackFormError(errorMessage) {
  trackEvent('evaluation_form_error', {
    error_message: errorMessage
  });
}

export function trackPageView(pageName) {
  trackEvent('page_view', {
    page_name: pageName
  });
}

export function trackMarketDataAccess() {
  trackEvent('market_data_accessed');
}

export function trackExpenseDefaults() {
  trackEvent('expense_defaults_used');
}

export function trackCalculationSubmitted({ paymentType, purchaseCategory } = {}) {
  trackEvent('calculation_submitted', {
    payment_type: paymentType,
    purchase_category: purchaseCategory
  });
}

export function trackResultViewed({ verdict, score } = {}) {
  trackEvent('result_viewed', {
    verdict,
    score
  });
}

export function trackEmailSubmitted({ score, verdict } = {}) {
  trackEvent('email_submitted', {
    score,
    verdict
  });
}

export function trackAffiliateLinkClicked({ linkType, verdict } = {}) {
  trackEvent('affiliate_link_clicked', {
    link_type: linkType,
    verdict
  });
}

export function trackShareClicked({ verdict, score } = {}) {
  trackEvent('share_clicked', {
    verdict,
    score
  });
}

const analytics = {
  initAnalytics,
  trackEvent,
  trackEvaluation,
  trackFeedback,
  trackFormSubmit,
  trackFormError,
  trackPageView,
  trackMarketDataAccess,
  trackExpenseDefaults,
  trackCalculationSubmitted,
  trackResultViewed,
  trackEmailSubmitted,
  trackAffiliateLinkClicked,
  trackShareClicked
};

export default analytics;
